import { useEffect, useState } from 'react';
import type { Player, Team } from '../types';
import { formatCurrency } from '../utils/helpers';

interface PlayerActionModalProps {
  player: Player | null;
  teams: Team[];
  isOpen: boolean;
  onClose: () => void;
  onAssign: (player: Player, teamId: string, price: number) => void;
  onRemove: (player: Player) => void;
  basePrice?: number;
}

const PlayerActionModal = ({ player, teams, isOpen, onClose, onAssign, onRemove, basePrice }: PlayerActionModalProps) => {
  const [selectedTeam, setSelectedTeam] = useState('');
  const [points, setPoints] = useState('');

  useEffect(() => {
    if (isOpen) {
      setPoints(basePrice ? String(basePrice) : '');
    }
  }, [isOpen, basePrice, player]);

  if (!isOpen || !player) return null;

  const handleAssign = () => {
    if (!selectedTeam || !points) return;
    onAssign(player, selectedTeam, parseInt(points));
    setSelectedTeam('');
    setPoints('');
    onClose();
  };

  const handleRemove = () => {
    onRemove(player);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
        <h3 className="text-xl font-bold text-slate-800 mb-4">Manage Player</h3>
        <div className="mb-4 bg-slate-50 p-3 rounded-lg border border-slate-200">
          <p className="font-bold text-indigo-700">{player.name}</p>
          <p className="text-sm text-slate-500">
            Currently: {player.status === 'sold'
              ? `Sold to ${teams.find(t => t.id === player.teamId)?.name} for ${formatCurrency(player.soldPrice)}`
              : 'Unsold'}
          </p>
        </div>

        {player.status === 'sold' ? (
          <div className="space-y-4">
            <p className="text-sm text-red-600 bg-red-50 p-3 rounded border border-red-100">
              Removing this player will refund {formatCurrency(player.soldPrice)} points to the team owner.
            </p>
            <div className="flex space-x-3">
              <button onClick={onClose} className="flex-1 py-2 border rounded-lg hover:bg-slate-50">
                Cancel
              </button>
              <button 
                onClick={handleRemove} 
                className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Remove & Refund
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Select Team</label>
              <select 
                className="w-full border rounded-lg p-2"
                value={selectedTeam}
                onChange={e => setSelectedTeam(e.target.value)}
              >
                <option value="">-- Select Team --</option>
                {teams.map(t => (
                  <option key={t.id} value={t.id}>{t.name} (Bal: {formatCurrency(t.currentBalance)})</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Bid Points</label>
              <input 
                type="number" 
                className="w-full border rounded-lg p-2"
                placeholder={basePrice ? String(basePrice) : '0'}
                value={points}
                onChange={e => setPoints(e.target.value)}
              />
              <p className="text-xs text-slate-500 mt-1">
                Defaulted from settings: {basePrice !== undefined ? formatCurrency(basePrice) : 'N/A'} pts
              </p>
            </div>
            <div className="flex space-x-3 pt-2">
              <button onClick={onClose} className="flex-1 py-2 border rounded-lg hover:bg-slate-50">
                Cancel
              </button>
              <button onClick={handleAssign} className="flex-1 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                Confirm Sale
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerActionModal;
